import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocalStorage } from "@uidotdev/usehooks"

const FORCE_OPEN_WITH_NATIVE_KEY = 'sharepoint_force_open_with_native'

// The setting is cached outside of React state, and persisted, so that code which
// does not re-render when it arrives (e.g. DataTables cell slots) can still link
// straight to the target URL instead of unconditionally routing through
// /sharepoint/open, which would cause a visible flash on every open.
let forceOpenWithNativeCache = localStorage.getItem(FORCE_OPEN_WITH_NATIVE_KEY) === '1'

// openUrlFor returns the URL used to open a file path. If documents should be
// opened with the native application, this points at the /sharepoint/open view,
// otherwise it's the href itself.
export const openUrlFor = (href) => {
    if (!forceOpenWithNativeCache || !href.includes('sharepoint.com')) {
        return href
    }
    const returnUrl = window.location.pathname + window.location.search
    return `/sharepoint/open?href=${encodeURIComponent(href)}&return_url=${encodeURIComponent(returnUrl)}`
}

export const openLinkTargetFor = (href) => {
    const url = openUrlFor(href)
    if (url.startsWith('/sharepoint/open')) {
        return '_self'
    }
    return '_blank'
}

export const setForceOpenWithNativeCache = (value) => {
    forceOpenWithNativeCache = value
    localStorage.setItem(FORCE_OPEN_WITH_NATIVE_KEY, value ? '1' : '0')
}

const useSharePointToken = () => {
  const location = useLocation()

  const [loading, setLoading] = useState(true)
  const [tokenIsValid, setTokenIsValid] = useState(false)
  const [encryptionKey, setEncryptionKey] = useLocalStorage("sharepoint_encryption_key", "")
  const [sharePointApplicationIsValid, setSharePointApplicationIsValid] = useState(true);
  const [sharePointApplicationError, setSharePointApplicationError] = useState("");
  const [sharePointApplicationLoading, setSharePointApplicationLoading] = useState(true);
  const [forceOpenWithNative, setForceOpenWithNative] = useState(forceOpenWithNativeCache);

  useEffect(() => {
    if( encryptionKey === "" ) {
      setEncryptionKey( newEncryptionKey() )
    }
  }, [])

  useEffect(() => {
    validateToken()
  }, [encryptionKey])
  
  // Need to ensure CADM Admin has configured the SharePoint integration properly
  useEffect(() => {
    fetch(`/v0/sharepoint_application/validate`, { credentials: 'same-origin' })
      .then(function(response) {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then(function(json) {
        if(json.error) {
          throw new Error(json.error)
        }

        const obj = json.result
        setSharePointApplicationIsValid( obj.is_valid )
        setSharePointApplicationError( obj.error )
        const force = obj.force_open_with_native || false
        forceOpenWithNativeCache = force
        localStorage.setItem(FORCE_OPEN_WITH_NATIVE_KEY, force ? '1' : '0')
        setForceOpenWithNative( force )
      })
      .catch(err => {
        console.log(`SharePoint Application validation`, err)
      })
      .finally(() => {
        setSharePointApplicationLoading(false)
      })
  }, [])
  
  const newEncryptionKey = () => {
    const randomString = [...crypto.getRandomValues(new Uint8Array(16))]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return randomString
  };
  
  const validateToken = async () => {
    const response = await fetch('/v0/sharepoint/token/validate?encryption_key=' + encryptionKey, { credentials: 'same-origin' })
    const json = await response.json()
    setTokenIsValid(json.is_valid)
    setLoading(false)
  };

  const redirectToAuthUrl = () => {
    const current = location.pathname + location.search
    fetch('/v0/sharepoint/token/auth_url?redirect_url=' + encodeURIComponent(current) + '&encryption_key=' + encryptionKey, { credentials: 'same-origin' })
                            .then(function(response) {
                              if (!response.ok) throw response
                              return response.json()
                            })
                            .then(function(json) {
                              window.location.href = json.auth_url
                            })
                            .catch(error => console.log("OAuth login error", error))
  }
  
  return {
    tokenIsValid,
    encryptionKey,
    redirectToAuthUrl,
    loading,
    sharePointApplicationIsValid,
    sharePointApplicationError,
    sharePointApplicationLoading,
    forceOpenWithNative,
  }
}

export default useSharePointToken
